import dayjs, { Dayjs } from 'dayjs'
import {
  EnrichedGeckoInspection,
  InboxFilterState,
  InspectionStatus,
} from 'features/inbox/types/types'
import { GeckoEntry } from 'types'

// Constants
const FUTURE_INSPECTION_THRESHOLD_WEEKS = 2

interface FilterOptions {
  excludeFilters?: ReadonlyArray<
    'status' | 'type' | 'dateRange' | 'search' | 'future' | 'dra'
  >
}

// Helper functions
/**
 * Extracts the inspection date from a GeckoEntry
 */
const getInspectionDate = (inspection: GeckoEntry): string | undefined => {
  return (
    (inspection.data?.start_date as string | undefined) ||
    (inspection.data?.date as string | undefined)
  )
}

/**
 * Extracts searchable text fields from an inspection
 */
const getSearchableText = (inspection: GeckoEntry): readonly string[] => {
  return [
    (inspection.data?.unitName as string) || '',
    (inspection.data?.componentName as string) || '',
    (inspection.data?.name as string) || '',
  ] as const
}

/**
 * Filters out future inspections beyond the threshold
 */
const filterFutureInspections = (
  inspections: ReadonlyArray<GeckoEntry>,
  showFuture: boolean
): ReadonlyArray<GeckoEntry> => {
  if (showFuture) return inspections

  const threshold = dayjs().add(FUTURE_INSPECTION_THRESHOLD_WEEKS, 'weeks')
  return inspections.filter((inspection) => {
    const dateValue = getInspectionDate(inspection)
    if (!dateValue) return true
    return dayjs(dateValue).isBefore(threshold)
  })
}

/**
 * Filters inspections by date range (inclusive)
 */
const filterByDateRange = (
  inspections: ReadonlyArray<GeckoEntry>,
  start: Dayjs,
  end: Dayjs
): ReadonlyArray<GeckoEntry> => {
  return inspections.filter((inspection) => {
    const dateValue = getInspectionDate(inspection)
    if (!dateValue) return false
    const date = dayjs(dateValue)
    return (
      (date.isAfter(start) || date.isSame(start, 'day')) &&
      (date.isBefore(end) || date.isSame(end, 'day'))
    )
  })
}

/**
 * Filters inspections by status
 */
const filterByStatus = (
  inspections: ReadonlyArray<GeckoEntry>,
  statuses: ReadonlyArray<InspectionStatus>
): ReadonlyArray<GeckoEntry> => {
  const statusSet = new Set(statuses)
  return inspections.filter((inspection) => {
    const status = inspection.data?.derivedStatus as
      | InspectionStatus
      | undefined
    return status && statusSet.has(status)
  })
}

/**
 * Filters inspections by type
 */
const filterByType = (
  inspections: ReadonlyArray<GeckoEntry>,
  types: ReadonlyArray<string>
): ReadonlyArray<GeckoEntry> => {
  const typeSet = new Set(types)
  return inspections.filter((inspection) => {
    const inspectionType = inspection.data?.inspection_type as
      | string
      | undefined
    return inspectionType && typeSet.has(inspectionType)
  })
}

/**
 * Filters inspections by DRA level
 */
const filterByDRALevel = (
  inspections: ReadonlyArray<GeckoEntry>,
  draLevels: ReadonlyArray<number>
): ReadonlyArray<GeckoEntry> => {
  const draSet = new Set(draLevels)
  return inspections.filter((inspection) => {
    const worstDRA = (inspection.data as EnrichedGeckoInspection['data'])
      ?.worstDRA
    return worstDRA !== undefined && draSet.has(worstDRA)
  })
}

/**
 * Filters inspections by search text
 */
const filterBySearchText = (
  inspections: ReadonlyArray<GeckoEntry>,
  searchText: string
): ReadonlyArray<GeckoEntry> => {
  const search = searchText.trim().toLowerCase()
  if (search.length === 0) return inspections

  return inspections.filter((inspection) => {
    const searchableFields = getSearchableText(inspection)
    return searchableFields.some((field) =>
      field.toLowerCase().includes(search)
    )
  })
}

/**
 * Applies all inbox filters to a list of inspections
 * @param inspections - The inspections to filter
 * @param filters - The filter state
 * @param options - Options to exclude specific filters
 * @returns Filtered inspections
 */
export const applyInboxFilters = (
  inspections:
    | ReadonlyArray<GeckoEntry>
    | ReadonlyArray<EnrichedGeckoInspection>,
  filters: InboxFilterState,
  options: FilterOptions = {}
): ReadonlyArray<GeckoEntry> => {
  const filtersToExclude = new Set(options.excludeFilters || [])
  const baseInspections = inspections as ReadonlyArray<GeckoEntry>

  const inspectionsFilteredByFuture = filtersToExclude.has('future')
    ? baseInspections
    : filterFutureInspections(baseInspections, filters.showFutureInspections)

  const inspectionsFilteredByDateRange =
    filtersToExclude.has('dateRange') || !filters.dateRange
      ? inspectionsFilteredByFuture
      : filterByDateRange(
          inspectionsFilteredByFuture,
          dayjs(filters.dateRange.start),
          dayjs(filters.dateRange.end)
        )

  const inspectionsFilteredByStatus =
    filtersToExclude.has('status') ||
    !filters.statusFilters ||
    filters.statusFilters.length === 0
      ? inspectionsFilteredByDateRange
      : filterByStatus(inspectionsFilteredByDateRange, filters.statusFilters)

  const inspectionsFilteredByType =
    filtersToExclude.has('type') ||
    !filters.inspectionTypeFilters ||
    filters.inspectionTypeFilters.length === 0
      ? inspectionsFilteredByStatus
      : filterByType(inspectionsFilteredByStatus, filters.inspectionTypeFilters)

  const inspectionsFilteredByDRA =
    filtersToExclude.has('dra') ||
    !filters.draLevelFilters ||
    filters.draLevelFilters.length === 0
      ? inspectionsFilteredByType
      : filterByDRALevel(inspectionsFilteredByType, filters.draLevelFilters)

  const inspectionsFilteredBySearch =
    filtersToExclude.has('search') || !filters.searchText
      ? inspectionsFilteredByDRA
      : filterBySearchText(inspectionsFilteredByDRA, filters.searchText)

  return inspectionsFilteredBySearch
}
